/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge } from 'react-bootstrap';

const GoogleMapsDiagnostic = () => {
  const [diagnostics, setDiagnostics] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const runDiagnostics = async () => {
      const results = {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        devicePixelRatio: window.devicePixelRatio,
        onlineStatus: navigator.onLine,
        connectionType: navigator.connection?.effectiveType || 'unknown',
        connectionDownlink: navigator.connection?.downlink || 'unknown',
        connectionRtt: navigator.connection?.rtt || 'unknown',
        platform: navigator.platform,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        apiKeyExists: !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        apiKeyLength: process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.length || 0,
        apiKeyPrefix: process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.substring(0, 10) || 'N/A',
        googleObjectExists: !!window.google,
        googleMapsExists: !!window.google?.maps,
        googlePlacesExists: !!window.google?.maps?.places,
        googleAutocompleteExists: !!window.google?.maps?.places?.Autocomplete,
        googleDirectionsExists: !!window.google?.maps?.DirectionsService,
        googleGeocoderExists: !!window.google?.maps?.Geocoder,
        isMobile: /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent),
        isSmallScreen: window.innerWidth <= 768,
        localStorageAvailable: (() => {
          try {
            localStorage.setItem('test', 'test');
            localStorage.removeItem('test');
            return true;
          } catch (e) {
            return false;
          }
        })(),
        sessionStorageAvailable: (() => {
          try {
            sessionStorage.setItem('test', 'test');
            sessionStorage.removeItem('test');
            return true;
          } catch (e) {
            return false;
          }
        })(),
        webGLSupported: (() => {
          try {
            const canvas = document.createElement('canvas');
            return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
          } catch (e) {
            return false;
          }
        })(),
        geolocationSupported: !!navigator.geolocation,
        httpsProtocol: window.location.protocol === 'https:',
        domain: window.location.hostname,
        protocol: window.location.protocol,
        port: window.location.port,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        referrer: document.referrer,
        title: document.title,
        readyState: document.readyState,
        lastModified: document.lastModified,
        cookieCount: document.cookie.split(';').filter(c => c.trim()).length,
        scriptCount: document.scripts.length,
        linkCount: document.links.length,
        imageCount: document.images.length,
        formCount: document.forms.length,
        iframeCount: document.getElementsByTagName('iframe').length,
        performanceAvailable: !!window.performance,
        performanceTiming: window.performance?.timing ? {
          navigationStart: window.performance.timing.navigationStart,
          loadEventEnd: window.performance.timing.loadEventEnd,
          domContentLoadedEventEnd: window.performance.timing.domContentLoadedEventEnd,
          loadTime: window.performance.timing.loadEventEnd - window.performance.timing.navigationStart,
          domLoadTime: window.performance.timing.domContentLoadedEventEnd - window.performance.timing.navigationStart
        } : null,
        memoryInfo: window.performance?.memory ? {
          usedJSHeapSize: window.performance.memory.usedJSHeapSize,
          totalJSHeapSize: window.performance.memory.totalJSHeapSize,
          jsHeapSizeLimit: window.performance.memory.jsHeapSizeLimit
        } : null,
        networkErrors: [],
        consoleErrors: []
      };

      // Test network connectivity
      try {
        const startTime = Date.now();
        const response = await fetch('https://maps.googleapis.com/maps/api/js', { 
          method: 'HEAD',
          mode: 'no-cors'
        });
        results.googleMapsReachable = true;
        results.googleMapsResponseTime = Date.now() - startTime;
      } catch (error) {
        results.googleMapsReachable = false;
        results.googleMapsError = error.message;
      }

      // Test API key validity (basic check)
      if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
        try {
          const testUrl = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
          const response = await fetch(testUrl, { method: 'HEAD' });
          results.apiKeyValid = response.ok;
          results.apiKeyStatus = response.status;
        } catch (error) {
          results.apiKeyValid = false;
          results.apiKeyError = error.message;
        }
      }

      setDiagnostics(results);
      setIsLoading(false);
    };

    runDiagnostics();
  }, []);

  const getStatusBadge = (value, type = 'boolean') => {
    if (type === 'boolean') {
      return value ? <Badge bg="success">✓</Badge> : <Badge bg="danger">✗</Badge>;
    }
    return <Badge bg="info">{value}</Badge>;
  };

  const copyDiagnostics = () => {
    const diagnosticText = JSON.stringify(diagnostics, null, 2);
    navigator.clipboard.writeText(diagnosticText).then(() => {
      alert('Diagnostics copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = diagnosticText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Diagnostics copied to clipboard!');
    });
  };

  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <h5>🔍 Google Maps Diagnostic Tool</h5>
        </Card.Header>
        <Card.Body>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Running diagnostics...</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5>🔍 Google Maps Diagnostic Tool</h5>
        <Button variant="outline-primary" size="sm" onClick={copyDiagnostics}>
          Copy Diagnostics
        </Button>
      </Card.Header>
      <Card.Body>
        <Alert variant="info">
          <strong>📱 Mobile Google Maps Loading Issues</strong><br/>
          This diagnostic tool helps identify why Google Maps might not be loading on mobile devices.
          Check the console for detailed logs and use this information to troubleshoot.
        </Alert>

        <div className="row">
          <div className="col-md-6">
            <h6>🔑 API Configuration</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>API Key Exists</td>
                  <td>{getStatusBadge(diagnostics.apiKeyExists)}</td>
                </tr>
                <tr>
                  <td>API Key Length</td>
                  <td>{getStatusBadge(diagnostics.apiKeyLength, 'text')}</td>
                </tr>
                <tr>
                  <td>API Key Prefix</td>
                  <td>{getStatusBadge(diagnostics.apiKeyPrefix, 'text')}</td>
                </tr>
                <tr>
                  <td>API Key Valid</td>
                  <td>{getStatusBadge(diagnostics.apiKeyValid)}</td>
                </tr>
                <tr>
                  <td>Google Maps Reachable</td>
                  <td>{getStatusBadge(diagnostics.googleMapsReachable)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-md-6">
            <h6>📱 Device Information</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>Is Mobile Device</td>
                  <td>{getStatusBadge(diagnostics.isMobile)}</td>
                </tr>
                <tr>
                  <td>Is Small Screen</td>
                  <td>{getStatusBadge(diagnostics.isSmallScreen)}</td>
                </tr>
                <tr>
                  <td>Screen Size</td>
                  <td>{getStatusBadge(diagnostics.screenSize, 'text')}</td>
                </tr>
                <tr>
                  <td>Device Pixel Ratio</td>
                  <td>{getStatusBadge(diagnostics.devicePixelRatio, 'text')}</td>
                </tr>
                <tr>
                  <td>Platform</td>
                  <td>{getStatusBadge(diagnostics.platform, 'text')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-md-6">
            <h6>🌐 Network & Connection</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>Online Status</td>
                  <td>{getStatusBadge(diagnostics.onlineStatus)}</td>
                </tr>
                <tr>
                  <td>Connection Type</td>
                  <td>{getStatusBadge(diagnostics.connectionType, 'text')}</td>
                </tr>
                <tr>
                  <td>HTTPS Protocol</td>
                  <td>{getStatusBadge(diagnostics.httpsProtocol)}</td>
                </tr>
                <tr>
                  <td>Domain</td>
                  <td>{getStatusBadge(diagnostics.domain, 'text')}</td>
                </tr>
                <tr>
                  <td>Google Maps Response Time</td>
                  <td>{getStatusBadge(diagnostics.googleMapsResponseTime ? `${diagnostics.googleMapsResponseTime}ms` : 'N/A', 'text')}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-md-6">
            <h6>🗺️ Google Maps Status</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>Google Object</td>
                  <td>{getStatusBadge(diagnostics.googleObjectExists)}</td>
                </tr>
                <tr>
                  <td>Google Maps</td>
                  <td>{getStatusBadge(diagnostics.googleMapsExists)}</td>
                </tr>
                <tr>
                  <td>Places API</td>
                  <td>{getStatusBadge(diagnostics.googlePlacesExists)}</td>
                </tr>
                <tr>
                  <td>Autocomplete</td>
                  <td>{getStatusBadge(diagnostics.googleAutocompleteExists)}</td>
                </tr>
                <tr>
                  <td>Directions Service</td>
                  <td>{getStatusBadge(diagnostics.googleDirectionsExists)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-md-6">
            <h6>🔧 Browser Capabilities</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>WebGL Supported</td>
                  <td>{getStatusBadge(diagnostics.webGLSupported)}</td>
                </tr>
                <tr>
                  <td>Geolocation Supported</td>
                  <td>{getStatusBadge(diagnostics.geolocationSupported)}</td>
                </tr>
                <tr>
                  <td>Local Storage</td>
                  <td>{getStatusBadge(diagnostics.localStorageAvailable)}</td>
                </tr>
                <tr>
                  <td>Session Storage</td>
                  <td>{getStatusBadge(diagnostics.sessionStorageAvailable)}</td>
                </tr>
                <tr>
                  <td>Performance API</td>
                  <td>{getStatusBadge(diagnostics.performanceAvailable)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-md-6">
            <h6>📊 Performance Info</h6>
            {diagnostics.performanceTiming && (
              <table className="table table-sm">
                <tbody>
                  <tr>
                    <td>Page Load Time</td>
                    <td>{getStatusBadge(`${diagnostics.performanceTiming.loadTime}ms`, 'text')}</td>
                  </tr>
                  <tr>
                    <td>DOM Load Time</td>
                    <td>{getStatusBadge(`${diagnostics.performanceTiming.domLoadTime}ms`, 'text')}</td>
                  </tr>
                  {diagnostics.memoryInfo && (
                    <>
                      <tr>
                        <td>Used JS Heap</td>
                        <td>{getStatusBadge(`${Math.round(diagnostics.memoryInfo.usedJSHeapSize / 1024 / 1024)}MB`, 'text')}</td>
                      </tr>
                      <tr>
                        <td>Total JS Heap</td>
                        <td>{getStatusBadge(`${Math.round(diagnostics.memoryInfo.totalJSHeapSize / 1024 / 1024)}MB`, 'text')}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="mt-3">
          <h6>🔍 Common Mobile Issues & Solutions</h6>
          <div className="alert alert-warning">
            <strong>Common Causes:</strong>
            <ul className="mb-0 mt-2">
              <li><strong>API Key Restrictions:</strong> Check if your Google Maps API key has mobile domain restrictions</li>
              <li><strong>HTTPS Required:</strong> Google Maps requires HTTPS on mobile devices</li>
              <li><strong>Network Issues:</strong> Slow or unreliable mobile connections can cause timeouts</li>
              <li><strong>Browser Compatibility:</strong> Some mobile browsers have limited Google Maps support</li>
              <li><strong>Memory Limitations:</strong> Mobile devices may have insufficient memory for Google Maps</li>
              <li><strong>Ad Blockers:</strong> Some mobile browsers block Google Maps scripts</li>
            </ul>
          </div>
        </div>

        <div className="mt-3">
          <h6>📋 User Agent</h6>
          <code className="d-block p-2 bg-light">{diagnostics.userAgent}</code>
        </div>
      </Card.Body>
    </Card>
  );
};

export default GoogleMapsDiagnostic;
